/**
 * LES AXES DE LA MATRICE DE DÉCLINAISONS — ce qui décide des lignes et des colonnes, HORS de l'écran.
 *
 * ⚠️ MODULE PUR, SÉPARÉ DE `Variants` : la matrice à deux axes est la seule partie du composant qui
 * DÉCIDE quelque chose — quelles lignes, dans quel ordre, quelle case naît au scan, combien de cases
 * une couleur ajoute — et un test ne doit pas monter un écran React pour vérifier qu'une longueur
 * hors grille reste visible. L'écran ne fait que dessiner ce que ce module a rangé.
 *
 * ⚠️ ON STOCKE SÉPARÉ, ON AFFICHE COMBINÉ : `size` et `size2` sont deux colonnes en base, et c'est
 * l'ARTICLE (par la grille de second axe qu'il porte) qui dit si la matrice a des lignes par couleur —
 * jamais la déclinaison. Le libellé industrie « 40/32 » n'existe qu'à l'écran.
 */
import type { ArticleVariant, SecondDimension } from '../api';
import { fieldLabel } from '../vocabulary';
import { t } from '../i18n';

/** Ce qui remplace une couleur, une taille ou une valeur absente — jamais une chaîne vide, invisible. */
export const SANS = '—';

/** Une case à créer, telle que la route de création la reçoit. */
export interface CellToCreate { color: string | null; size: string | null; size2: string | null }

/** La clé d'une case — UN SEUL endroit la compose, sinon deux formats finissent par coexister. */
export const variantKey = (color: string | null, size: string | null, size2: string | null): string =>
  `${color ?? SANS}|${size ?? SANS}|${size2 ?? SANS}`;

/**
 * LES CASES QUI PORTENT PLUSIEURS DÉCLINAISONS — clé de case → nombre de déclinaisons EN PLUS de celle qu'elle montre.
 * ⚠️ UNE CASE N'EN MONTRE QU'UNE : deux déclinaisons sans couleur ni taille (un article rattaché par erreur à un autre)
 *    tombaient sur la même case, et la matrice en cachait une sans un mot. La vue Liste les montre toutes ;
 *    ce compte le DIT, sur la case et au-dessus de la matrice. `withSize2` : la seconde dimension entre dans la case.
 */
export function sharedCases(
  rows: readonly { color: string | null; size: string | null; size2: string | null }[], withSize2: boolean,
): Map<string, number> {
  const n = new Map<string, number>();
  for (const v of rows) {
    const k = variantKey(v.color, v.size, withSize2 ? v.size2 : null);
    n.set(k, (n.get(k) ?? 0) + 1);
  }
  return new Map([...n].filter(([, c]) => c > 1).map(([k, c]) => [k, c - 1]));
}

/**
 * LE LIBELLÉ INDUSTRIE d'une déclinaison à deux axes : « 40/32 » — la taille, puis la valeur sans son
 * `L` de tête (« L32 » se lit « 32 pouces » dans le métier). Sans seconde dimension, la taille seule.
 */
export const industryLabel = (size: string | null, size2: string | null): string =>
  (size2 ? `${size ?? SANS}/${size2.replace(/^L/, '')}` : (size ?? SANS));

/**
 * LES COLONNES : l'ordre DÉCLARÉ par la grille d'abord, puis les tailles vues hors grille dans leur
 * ordre d'apparition. Rangées par l'alphabet, L passerait avant M et S avant XS ; sans grille, on
 * retombe sur l'apparition — et on ne prétend pas que c'est un ordre de tailles.
 */
export function sizeColumns(rows: readonly Pick<ArticleVariant, 'size'>[], sizeOrder: readonly string[]): string[] {
  const seen = [...new Set(rows.map((v) => v.size ?? SANS))];
  const declared = sizeOrder.filter((size) => seen.includes(size));
  return [...declared, ...seen.filter((size) => !declared.includes(size))];
}

/**
 * UN CODE SAISI DANS L'ONGLET DÉCLINAISONS ET PAS ENCORE ENREGISTRÉ — une case à créer, ou le code corrigé d'une
 * déclinaison existante.
 * ⚠️ `from` DIT LAQUELLE DES DEUX : le code actuel de la déclinaison corrigée, `null` pour une case à créer. L'onglet le
 *    sait parce qu'il lit les déclinaisons ; la fiche qui enregistre ne les lit pas, et deux routes différentes écrivent.
 */
export interface DraftCode { color: string | null; size: string | null; size2: string | null; gencode: string; from: string | null }

/**
 * CE QUE L'ONGLET DÉCLINAISONS GARDE EN ATTENTE jusqu'à « Enregistrer » — les couleurs ajoutées (`fXCols` de la
 * maquette) et les codes saisis (`fEanOv`), écrits par le pied de la fiche et effacés par son « Annuler ».
 * ⚠️ RIEN N'EST ÉCRIT AVANT, comme au dessin : la grille choisie part avec le même pied, et une case scannée sur une
 *    taille de la nouvelle grille ne peut s'écrire qu'une fois la grille enregistrée.
 */
export interface VariantDraft {
  /** Les couleurs ajoutées sans gencodes automatiques : leur ligne existe en pointillé tant que rien n'y est scanné. */
  colors: readonly string[];
  /** Les codes saisis, par case (`variantKey`). */
  codes: Readonly<Record<string, DraftCode>>;
}

export const EMPTY_VARIANT_DRAFT: VariantDraft = { colors: [], codes: {} };

/** Combien de déclinaisons l'enregistrement créera ou corrigera — une couleur sans case scannée n'en compte aucune. */
export const draftChanges = (draft: VariantDraft): number => Object.keys(draft.codes).length;

/**
 * LES COLONNES DE LA FICHE — la règle de la maquette (`fDispSizes`).
 * En scan, TOUTES les tailles de la grille : c'est là que naissent celles qui manquent. Hors scan, celles qu'au moins
 * une déclinaison porte, enregistrée ou en attente — et toute la grille si aucune n'en porte, le cas d'une grille qu'on
 * vient de choisir sur un article qui n'avait pas de tailles.
 * ⚠️ PUIS LES TAILLES PORTÉES HORS GRILLE, dans leur ordre d'apparition : la maquette les perd en changeant de grille,
 *    ici ce sont des déclinaisons réelles, avec leur stock, et le serveur refusera la grille qui les ignore.
 */
export function sheetSizeColumns(
  gridSizes: readonly string[],
  rows: readonly Pick<ArticleVariant, 'size'>[],
  draft: VariantDraft,
  scan: boolean,
): string[] {
  const seen = [...new Set([...rows.map((v) => v.size ?? SANS), ...Object.values(draft.codes).map((c) => c.size ?? SANS)])];
  const carried = gridSizes.filter((size) => seen.includes(size));
  const declared = scan || carried.length === 0 ? gridSizes : carried;
  return [...declared, ...seen.filter((size) => !gridSizes.includes(size))];
}

/** Une déclinaison telle que le scan la compare : sa case et son code. */
type ScannedRow = Pick<ArticleVariant, 'color' | 'size' | 'size2' | 'gencode'>;

/**
 * LA CASE QUI PORTE DÉJÀ CE CODE SUR CET ARTICLE, ou `null` — le refus de la maquette (« Scan refusé — le gencode … est
 * déjà affecté à … sur cet article »).
 * ⚠️ LE CODE EFFECTIF DE CHAQUE CASE : une correction en attente remplace celui de la base, et le libère.
 * ⚠️ UN CODE PORTÉ PAR UN AUTRE ARTICLE ne se voit pas d'ici : le serveur le refuse à l'enregistrement, en le nommant.
 */
export function scanConflict(
  rows: readonly ScannedRow[],
  draft: VariantDraft,
  cellKey: string,
  code: string,
  withSecondAxis: boolean,
): { color: string | null; size: string | null; size2: string | null } | null {
  const effective = new Map<string, { color: string | null; size: string | null; size2: string | null; code: string | null }>();
  for (const v of rows) {
    const size2 = withSecondAxis ? v.size2 : null;
    effective.set(variantKey(v.color, v.size, size2), { color: v.color, size: v.size, size2, code: v.gencode });
  }
  for (const [k, c] of Object.entries(draft.codes)) effective.set(k, { color: c.color, size: c.size, size2: c.size2, code: c.gencode });
  for (const [k, cell] of effective) {
    if (k !== cellKey && cell.code === code) return { color: cell.color, size: cell.size, size2: cell.size2 };
  }
  return null;
}

/**
 * CE QU'UN CODE SAISI DANS UNE CASE FAIT AU BROUILLON.
 * Vide, ou égal au code que la déclinaison porte en base : la case revient à la base. Sinon il remplace la saisie
 * précédente de la case — une création si la déclinaison n'existe pas, une correction si elle existe.
 */
export function draftWithCode(
  draft: VariantDraft,
  cell: { color: string | null; size: string | null; size2: string | null },
  existing: Pick<ArticleVariant, 'gencode'> | undefined,
  code: string,
): VariantDraft {
  const key = variantKey(cell.color, cell.size, cell.size2);
  const codes = { ...draft.codes };
  if (!code || (existing && code === existing.gencode)) delete codes[key];
  else codes[key] = { ...cell, gencode: code, from: existing?.gencode ?? null };
  return { ...draft, codes };
}

/** Le brouillon sans les cases données — celles qu'un envoi vient d'écrire. */
export function draftWithout(draft: VariantDraft, keys: readonly string[]): VariantDraft {
  const codes = { ...draft.codes };
  for (const k of keys) delete codes[k];
  return { ...draft, codes };
}

/** Une case à créer à l'enregistrement, avec sa clé de brouillon pour l'en retirer une fois écrite. */
export interface DraftCreation extends CellToCreate { key: string; gencode: string }

/**
 * CE QUE « ENREGISTRER » ÉCRIT POUR LES DÉCLINAISONS : les corrections de code, puis les créations PAR COULEUR.
 * ⚠️ UN ENVOI PAR COULEUR : chaque envoi est tout ou rien côté serveur, et une couleur est l'unité que l'opérateur a
 *    ajoutée ou scannée — un refus nomme la sienne, et les autres couleurs, déjà écrites, restent écrites.
 */
export function draftWork(draft: VariantDraft): {
  renames: { key: string; from: string; to: string }[];
  creations: { color: string | null; cells: DraftCreation[] }[];
} {
  const renames: { key: string; from: string; to: string }[] = [];
  const byColor = new Map<string | null, DraftCreation[]>();
  for (const [key, c] of Object.entries(draft.codes)) {
    if (c.from !== null) { renames.push({ key, from: c.from, to: c.gencode }); continue; }
    const cells = byColor.get(c.color) ?? [];
    cells.push({ key, color: c.color, size: c.size, size2: c.size2, gencode: c.gencode });
    byColor.set(c.color, cells);
  }
  return { renames, creations: [...byColor].map(([color, cells]) => ({ color, cells })) };
}

/**
 * LES LIGNES DE CHAQUE BLOC DE COULEUR : les valeurs DÉCLARÉES de la dimension, dans leur ordre, puis
 * ce que les déclinaisons portent EN PLUS — une valeur hors grille est une déclinaison réelle, la
 * taire ferait disparaître un stock — et « — » en dernier si des déclinaisons n'ont AUCUNE seconde
 * dimension sur une grille qui en a une (une reprise qui n'a pas su découper, typiquement).
 * ⚠️ Toutes les valeurs déclarées sont rendues, même sans déclinaison : c'est précisément la ligne
 * « non créée » que le scan ouvre.
 */
export function secondAxisRows(rows: readonly Pick<ArticleVariant, 'size2'>[], dimension: SecondDimension): string[] {
  const seen = [...new Set(rows.map((v) => v.size2 ?? SANS))];
  const extra = seen.filter((v) => v !== SANS && !dimension.values.includes(v));
  return [...dimension.values, ...extra, ...(seen.includes(SANS) ? [SANS] : [])];
}

/** Les trois états d'une case, ceux de la légende du dessin. */
export type CellState = 'stocked' | 'created' | 'missing';

/** L'état d'une case. ⚠️ `null` (jamais comptée) n'est PAS « en stock » : sans mesure, rien n'est en stock. */
export const cellState = (v: Pick<ArticleVariant, 'stockQty'> | undefined): CellState =>
  (!v ? 'missing' : (v.stockQty ?? 0) > 0 ? 'stocked' : 'created');

/** Le total d'un bloc de couleur — en pièces MESURÉES, et le nombre de cases jamais comptées à part. */
export function blockTotal(rows: readonly Pick<ArticleVariant, 'stockQty'>[]): { pieces: number; neverCounted: number } {
  return {
    pieces: rows.reduce((n, v) => n + (v.stockQty ?? 0), 0),
    neverCounted: rows.filter((v) => v.stockQty === null).length,
  };
}

/**
 * LES CASES QU'« AJOUTER » CRÉE pour une couleur : toute la grille, croisée avec toute la dimension.
 * ⚠️ SUR LES VALEURS DÉCLARÉES SEULEMENT — jamais sur « — » ni sur une valeur hors grille : celles-là
 * n'existent à l'écran que parce qu'une reprise les y a mises, et les recréer les multiplierait. Une
 * dimension sans valeur déclarée retombe sur une ligne à un axe : rien n'est inventé à sa place.
 * ⚠️ `SANS` redevient `null` : c'est un signe d'écran, pas une taille.
 */
export function lineToCreate(color: string, sizes: readonly string[], dimension: SecondDimension | null): CellToCreate[] {
  const values: (string | null)[] = dimension && dimension.values.length > 0 ? dimension.values : [null];
  return sizes.flatMap((size) => values.map((size2) => ({ color, size: size === SANS ? null : size, size2 })));
}

/**
 * LE RÉSUMÉ DES TAILLES D'UN MODÈLE pour le journal : « 38 → 46 », « M », et « × 30/32/34 » quand la
 * grille porte un second axe — la colonne « Tailles » de la maquette « Articles — Tailles 2 ».
 * ⚠️ PREMIÈRE ET DERNIÈRE VIENNENT DU SERVEUR, dans l'ordre de la grille : l'écran ne trie rien. Une
 *    dimension longue est ABRÉGÉE et le dit (« 18/20/22/24 +6 ») — dix valeurs ne tiennent pas dans
 *    une cellule, et une cellule tronquée en silence se lirait comme une grille de quatre longueurs.
 */
export function sizeSpanLabel(first: string | null, last: string | null, dimension: SecondDimension | null): string | null {
  if (!first) return null;
  const span = last && last !== first ? `${first} → ${last}` : first;
  if (!dimension || dimension.values.length === 0) return span;
  const values = dimension.values;
  const shown = values.length > 4 ? `${values.slice(0, 4).join('/')} +${values.length - 4}` : values.join('/');
  return `${span} × ${shown}`;
}

/**
 * CE QUE LA TÊTE DES DÉCLINAISONS ANNONCE : quelle grille, et combien d'axes.
 *
 * ⚠️ « AUCUN ORDRE DÉCLARÉ » N'EST PAS « AUCUNE GRILLE », et les confondre fait dire à l'écran le
 * contraire de la base : le serveur rend un ordre VIDE aussi bien quand rien n'est rattaché que
 * quand la grille rattachée n'a encore aucune taille active (une grille neuve, typiquement). Le
 * modèle, lui, porte bien sa grille — l'annoncer « sans grille rattachée » envoie en rattacher une
 * seconde par-dessus la première.
 */
export function axesSummary(
  gridName: string | null,
  sizeOrder: readonly string[],
  dimension: SecondDimension | null,
): string {
  // ⚠️ LA TÊTE DÉCRIT LES AXES QUE LA MATRICE DESSINE, et la matrice suit les grilles CHOISIES, enregistrées ou non
  //    (maquette : `fArt` lit `fEdit.tgrid`, `d2Of` lit `fEdit.lgrid`) — ce qui n'est pas encore écrit, le pied le dit.
  // ⚠️ LA PHRASE S’ASSEMBLE DE CLAUSES COMPLÈTES, jamais de trous mis bout à bout. Une clé qui
  //    commence par `{head}{order}` ne dit rien au traducteur : il ne peut ni déplacer le préfixe,
  //    ni savoir quelle phrase arrive dans le trou. Ici chaque morceau se traduit seul, et ce sont
  //    le tiret et la virgule — de la ponctuation, pas du texte — qui les relient.
  const head = gridName ? `${t('Grille « {gridName} »', { gridName })} — ` : '';
  const values = dimension && dimension.values.length ? `(${dimension.values.join(', ')})` : t('(aucune valeur déclarée)');
  if (sizeOrder.length === 0) {
    const order = gridName
      ? t('aucune taille déclarée : celles des déclinaisons se lisent dans leur ordre d’apparition')
      : t('Sans grille rattachée : les tailles se lisent dans leur ordre d’apparition');
    const axe = dimension
      ? t('second axe « {v} » {values}.', { v: dimension.label.toLowerCase(), values })
      : t('sans second axe.');
    return `${head}${order}, ${axe}`;
  }
  const axe = dimension
    ? t('tailles × {v} {values}.', { v: dimension.label.toLowerCase(), values })
    : t('un seul axe de tailles.');
  return `${head}${axe}`;
}

/**
 * POURQUOI LA DIMENSION 2 N'EST PAS OFFERTE — la phrase que l'écran met à la place du choix.
 *
 * ⚠️ CE QUI N'EST PAS OFFERT SE DIT, ET DIT POURQUOI (§23.0) : un contrôle inerte sans motif envoie
 * chercher la fonction ailleurs, et un contrôle qui DISPARAÎT fait conclure qu'elle n'existe pas.
 *
 * ⚠️ IL NE DÉPEND PLUS DE LA GRILLE DE TAILLES, et c'est le renversement en une ligne : le second axe
 * est un champ de l'ARTICLE, au même titre que sa grille. Le subordonner à la première reviendrait à
 * refuser un modèle qui n'a que des longueurs, et surtout à laisser croire que les deux sont liés.
 *
 * ⚠️ IL RESTE UN CAS QUI BLOQUE, ET UN SEUL : des déclinaisons qui ne portent pas toutes le même
 * second axe. Poser un choix unique par-dessus écraserait en silence celui d'une partie du modèle.
 * ⚠️ Rendre `null` quand le choix EST offert : c'est l'appelant qui dessine alors le picker.
 */
export function secondAxisBlocker(state: {
  mixedAxes: boolean;
  gridsLoaded: boolean;
}): string | null {
  if (state.mixedAxes) return t('{v} : les déclinaisons de ce modèle n’en portent pas toutes le même — reprenez-les une à une avant d’en poser un pour tout le modèle.', { v: fieldLabel('sizeGrid2') });
  if (!state.gridsLoaded) return t('Lecture des grilles…');
  return null;
}
